import { useRef, useState, type FormEvent } from "react";
import type { ContextSource, RecipeMeResponse } from "@bitacorapro/api-types";
import {
  CONTEXT_MAX_BYTES,
  uploadContextSource,
  uploadableKinds,
  validateContextFile,
  type ContextSourceKindView,
} from "../features/context/contextUpload";
import { Drawer } from "./Drawer";

// «Subir documento» (ADR-0173 fase 2, PR-6). Kind → file → optional
// validity for dated kinds → hash + presigned PUT + registration. The
// drawer reports progress in words because the three steps can each take
// seconds on a 20 MB PDF; the server's own message surfaces on failure
// (the wire module already maps the codes a person can act on).

type Props = {
  brandId: string;
  recipe: RecipeMeResponse | undefined;
  onClose: () => void;
  onUploaded: (source: ContextSource) => void;
};

type Phase =
  | { kind: "idle" }
  | { kind: "busy"; step: "hashing" | "uploading" | "registering" }
  | { kind: "error"; message: string };

const STEP_LABEL = {
  hashing: "Calculando huella del archivo…",
  uploading: "Subiendo al almacenamiento privado…",
  registering: "Registrando y enviando a extracción…",
} as const;

export function ContextSourceUploadDrawer({ brandId, recipe, onClose, onUploaded }: Props) {
  const kinds = uploadableKinds(recipe);
  const [kindKey, setKindKey] = useState<string>(kinds[0]?.key ?? "");
  const [file, setFile] = useState<File | null>(null);
  const [validFrom, setValidFrom] = useState("");
  const [validTo, setValidTo] = useState("");
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const kindRef = useRef<HTMLSelectElement>(null);
  const kind: ContextSourceKindView | undefined = kinds.find((k) => k.key === kindKey);

  const fileError = file && kind ? validateContextFile(file, kind) : null;
  const datesError =
    validFrom && validTo && validFrom > validTo ? "La vigencia termina antes de empezar." : null;
  const canSubmit = phase.kind !== "busy" && !!kind && !!file && !fileError && !datesError;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit || !kind || !file) return;
    setPhase({ kind: "busy", step: "hashing" });
    try {
      const source = await uploadContextSource({
        brandId,
        kind,
        file,
        validFrom: kind.validity === "dated" && validFrom ? validFrom : undefined,
        validTo: kind.validity === "dated" && validTo ? validTo : undefined,
        onProgress: (step) => setPhase({ kind: "busy", step }),
      });
      onUploaded(source);
    } catch (err) {
      setPhase({ kind: "error", message: err instanceof Error ? err.message : String(err) });
    }
  }

  return (
    <Drawer
      title="Subir documento del cliente"
      onClose={onClose}
      initialFocusRef={kindRef}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="submit"
            form="context-upload-form"
            className="btn btn--primary"
            disabled={!canSubmit}
          >
            {phase.kind === "busy" ? "Subiendo…" : "Subir y extraer reglas"}
          </button>
        </>
      }
    >
      <form id="context-upload-form" onSubmit={(e) => void submit(e)} className="stack-gap">
        {kinds.length === 0 ? (
          <div className="empty">
            <div className="empty__title">Esta vertical no define documentos de contexto.</div>
          </div>
        ) : (
          <>
            <label className="field">
              <span className="field__label">Tipo de documento</span>
              <select
                ref={kindRef}
                className="select"
                value={kindKey}
                onChange={(e) => {
                  setKindKey(e.target.value);
                  setFile(null);
                }}
                disabled={phase.kind === "busy"}
              >
                {kinds.map((k) => (
                  <option key={k.key} value={k.key}>
                    {k.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field__label">Archivo</span>
              <input
                type="file"
                accept={kind?.mimeTypes.join(",")}
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                disabled={phase.kind === "busy"}
              />
              <span className="cell-meta">
                {kind?.mimeTypes.includes("application/pdf") ? "PDF" : ""}
                {kind?.mimeTypes.some((m) => m.includes("spreadsheetml")) ? " · Excel (.xlsx)" : ""}
                {" · hasta "}
                {Math.round(CONTEXT_MAX_BYTES / 1024 / 1024)} MB
              </span>
              {fileError && (
                <span className="field__hint" role="alert">
                  {fileError}
                </span>
              )}
            </label>
            {kind?.validity === "dated" && (
              <div className="field">
                <span className="field__label">Vigencia</span>
                <div className="stack-gap" style={{ flexDirection: "row", gap: "var(--s-3)" }}>
                  <input
                    className="input"
                    type="date"
                    aria-label="Vigente desde"
                    value={validFrom}
                    onChange={(e) => setValidFrom(e.target.value)}
                    disabled={phase.kind === "busy"}
                  />
                  <input
                    className="input"
                    type="date"
                    aria-label="Vigente hasta"
                    value={validTo}
                    onChange={(e) => setValidTo(e.target.value)}
                    disabled={phase.kind === "busy"}
                  />
                </div>
                {datesError && (
                  <span className="field__hint" role="alert">
                    {datesError}
                  </span>
                )}
              </div>
            )}
            <p className="field__hint">
              El documento se guarda en un almacenamiento privado y la IA extrae reglas atómicas que
              podrás revisar antes de activarlas. Los datos personales se enmascaran antes de llegar
              al modelo.
            </p>
            {phase.kind === "busy" && (
              <p className="cell-meta" role="status" aria-live="polite">
                {STEP_LABEL[phase.step]}
              </p>
            )}
            {phase.kind === "error" && (
              <div className="empty" role="alert">
                <div className="empty__title">No se pudo subir</div>
                <div className="empty__sub">{phase.message}</div>
              </div>
            )}
          </>
        )}
      </form>
    </Drawer>
  );
}
